import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, PiggyBank, Trash2 } from 'lucide-react';
import MobilePageHeader from '../../shared/mobile/MobilePageHeader';
import RestaurantSelector from '../../common/RestaurantSelector';
import { useAnalysisStore } from '../../../stores/appAnalysisStore';
import { eur } from '../../orderhistory/utils';
import { cn } from '../../../lib/utils';
import {
  borderGrey,
  cardClass,
  darkOrangeTextColor,
  elevatedBg,
  greenTextColor,
  primaryText,
  secondaryText,
  semiBoldText,
  tertiaryText,
} from '../../../styling/stylingHelper';
import AnalysisReportsSection from './reports/AnalysisReportsSection';
import { FoodDrinksToggle, matchesFoodDrinks } from './shared/AnalysisControls';
import { useAnalysisSelection } from './shared/useAnalysisSelection';

/**
 * /analysis landing: reviewed findings, Overviews (super admin identity only
 * in v1) and on-demand Reports.
 */
const AnalysisPage: React.FC = () => {
  const { t } = useTranslation();
  const { restaurantSelection, isAllRestaurants, isSuperAdmin } = useAnalysisSelection();
  const markSeen = useAnalysisStore(s => s.markAnalysisFindingsSeen);
  useEffect(() => { markSeen(); }, [markSeen]);

  return (
    <div>
      <MobilePageHeader title={t('analysis.title')} right={<RestaurantSelector />} />
      <div className="space-y-6">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h1 className={cn('text-xl lg:text-2xl', semiBoldText, primaryText)}>{t('analysis.title')}</h1>
            <p className={cn('text-sm mt-0.5', secondaryText)}>{t('analysis.subtitle')}</p>
          </div>
          <FoodDrinksToggle />
        </div>

        {isSuperAdmin && <OverviewCards />}

        <hr className={cn('border-t', borderGrey)} />

        <AnalysisReportsSection restaurantId={isAllRestaurants ? null : restaurantSelection || null} />
      </div>
    </div>
  );
};

/** Waste + Money savers overview cards with their headline totals (respect the global filter). */
const OverviewCards: React.FC = () => {
  const { t } = useTranslation();
  const { restaurantSelection, isAllRestaurants } = useAnalysisSelection();
  const filter = useAnalysisStore(s => s.foodDrinksFilter);
  const moneySavers = useAnalysisStore(s => s.moneySaversBySelection[restaurantSelection]);
  const moneySaversLoading = useAnalysisStore(s => !!s.moneySaversLoading[restaurantSelection]);
  const loadMoneySavers = useAnalysisStore(s => s.loadMoneySavers);
  const findings = useAnalysisStore(s => s.wasteFindings[restaurantSelection]);

  useEffect(() => {
    if (restaurantSelection) loadMoneySavers(restaurantSelection);
  }, [restaurantSelection, loadMoneySavers]);

  const wasteFindings = useMemo(
    () => (isAllRestaurants ? [] : findings ?? []).filter(g => matchesFoodDrinks(filter, g.side)),
    [findings, filter, isAllRestaurants]
  );
  const savingsTotal = useMemo(
    () => (moneySavers?.groups ?? []).filter(g => matchesFoodDrinks(filter, g.side)).reduce((s, g) => s + g.savings, 0),
    [moneySavers, filter]
  );

  return (
    <section className="space-y-3">
      <h2 className={cn('text-lg', semiBoldText, primaryText)}>{t('analysis.overviews.title')}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <OverviewCard
          to="/analysis/waste"
          icon={<Trash2 className={cn('w-5 h-5', darkOrangeTextColor)} />}
          title={t('analysis.waste.cardTitle')}
          description={t('analysis.waste.cardDescription')}
          value={wasteFindings.length ? eur(wasteFindings.reduce((sum, finding) => sum + Math.max(0, finding.wasteEur), 0)) : ''}
          valueLabel={t(wasteFindings.length ? 'analysis.waste.cardValueLabel' : 'analysis.waste.empty')}
          valueClassName={darkOrangeTextColor}
        />
        <OverviewCard
          to="/analysis/money-savers"
          icon={<PiggyBank className={cn('w-5 h-5', greenTextColor)} />}
          title={t('analysis.moneySavers.cardTitle')}
          description={t('analysis.moneySavers.cardDescription')}
          value={moneySaversLoading && !moneySavers ? null : eur(savingsTotal)}
          valueLabel={t('analysis.moneySavers.cardValueLabel')}
          valueClassName={greenTextColor}
        />
      </div>
    </section>
  );
};

interface OverviewCardProps {
  to: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  /** null = still loading */
  value: string | null;
  valueLabel: string;
  valueClassName: string;
}

const OverviewCard: React.FC<OverviewCardProps> = ({ to, icon, title, description, value, valueLabel, valueClassName }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => navigate(to)}
      className={cn(cardClass, 'border p-5 text-left flex flex-col gap-3 hover:shadow-md transition-shadow', borderGrey)}
    >
      <div className="flex items-center gap-3">
        <span className={cn('flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0', elevatedBg)}>{icon}</span>
        <div className="min-w-0">
          <p className={cn('text-base', semiBoldText, primaryText)}>{title}</p>
          <p className={cn('text-xs', secondaryText)}>{description}</p>
        </div>
      </div>
      <div className="flex items-baseline gap-2">
        {value === null
          ? <span className={cn('h-7 w-24 rounded animate-pulse', elevatedBg)} />
          : <span className={cn('text-2xl font-bold', valueClassName)}>{value}</span>}
        <span className={cn('text-xs', tertiaryText)}>{valueLabel}</span>
      </div>
      <span className={cn('inline-flex items-center gap-1 text-sm', semiBoldText, greenTextColor)}>
        {t('analysis.overviews.open')}
        <ArrowRight className="w-4 h-4" />
      </span>
    </button>
  );
};

export default AnalysisPage;
